import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  BugReport as BugIcon,
  RateReview as FeedbackIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { submitFeedback, Sentry } from '../../config/sentry';

export type FeedbackDialogMode = 'bug' | 'feedback';

interface FeedbackDialogProps {
  open: boolean;
  defaultMode?: FeedbackDialogMode;
  /** When set, the dialog is in crash-report mode: tabs are hidden, bug tab is locked,
   *  and feedback is linked to the existing Sentry crash event via associatedEventId. */
  associatedEventId?: string | null;
  onClose: () => void;
}

// Aligned with Sentry's SeverityLevel (excluding 'debug' which isn't meaningful for user-reported bugs)
type Severity = 'info' | 'warning' | 'error' | 'fatal';

// C4-605: Severity selector removed from the bug-reporting UI per client request;
// bug severity now always defaults to 'error'. Lookup tables kept (commented) for easy re-enable.
// const SEVERITY_OPTIONS: Severity[] = ['info', 'warning', 'error', 'fatal'];
//
// const SEVERITY_COLOR: Record<Severity, 'info' | 'warning' | 'error' | 'default'> = {
//   info: 'info',
//   warning: 'warning',
//   error: 'error',
//   fatal: 'error',
// };
//
// const SEVERITY_LABEL: Record<Severity, string> = {
//   info: 'Info',
//   warning: 'Warning',
//   error: 'Error',
//   fatal: 'Fatal',
// };

const BUG_TITLE_MAX = 80;
const BUG_DESC_MAX = 1000;
const FEEDBACK_MSG_MAX = 1000;

const FEEDBACK_CATEGORIES = [
  { value: 'general', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'performance', label: 'Performance Issue' },
  { value: 'ui_ux', label: 'UI / UX' },
  { value: 'other', label: 'Other' },
] as const;

type FeedbackCategory = typeof FEEDBACK_CATEGORIES[number]['value'];

// ── Main Dialog ───────────────────────────────────────────────────────────────
const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, defaultMode = 'bug', associatedEventId, onClose }) => {
  const location = useLocation();
  const { user } = useAuthStore();
  const isCrashMode = Boolean(associatedEventId);

  const [activeTab, setActiveTab] = useState<0 | 1>(defaultMode === 'bug' ? 0 : 1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Bug report fields
  const [bugTitle, setBugTitle] = useState('');
  const [bugDescription, setBugDescription] = useState('');
  const [bugSeverity, setBugSeverity] = useState<Severity>('error');

  // Feedback fields
  const [feedbackCategory, setFeedbackCategory] = useState<FeedbackCategory>('general');
  const [feedbackMessage, setFeedbackMessage] = useState('');

  // Sync tab and reset form each time the dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(defaultMode === 'bug' ? 0 : 1);
      setSubmitted(false);
      setBugTitle('');
      setBugDescription('');
      setBugSeverity('error');
      setFeedbackCategory('general');
      setFeedbackMessage('');
    }
  }, [open, defaultMode]);

  const userName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  const handleSubmitBug = async () => {
    if (!bugTitle.trim() || !bugDescription.trim()) return;
    setLoading(true);
    try {
      if (isCrashMode && associatedEventId) {
        Sentry.withScope((scope) => {
          scope.setTag('type', 'bug_report');
          scope.setTag('severity', bugSeverity);
          scope.setTag('page', location.pathname);
          scope.setLevel(bugSeverity);
          if (user?.email || userName) {
            scope.setUser({ email: user?.email, username: userName || undefined });
          }
          Sentry.captureFeedback({
            name: userName || 'Anonymous',
            email: user?.email || '',
            message: `[${bugSeverity.toUpperCase()}] ${bugTitle.trim()}\n\n${bugDescription.trim()}`,
            url: globalThis.location.href,
            associatedEventId,
          });
        });
      } else {
        Sentry.withScope((scope) => {
          scope.setTag('type', 'bug_report');
          scope.setTag('severity', bugSeverity);
          scope.setTag('page', location.pathname);
          scope.setLevel(bugSeverity);
          scope.setContext('bug_report', {
            title: bugTitle.trim(),
            description: bugDescription.trim(),
            page: location.pathname,
            userAgent: navigator.userAgent,
          });
          if (user?.email || userName) {
            scope.setUser({ email: user?.email, username: userName || undefined });
          }
          // User Feedback (not an Issue event) so it lands in Sentry's feedback
          // channel with a replay attached when one is active; type/severity/page
          // tags drive filtering and alerts (e.g. email maintenance on bug_report).
          Sentry.captureFeedback({
            name: userName || 'Anonymous',
            email: user?.email || '',
            message: `[${bugSeverity.toUpperCase()}] ${bugTitle.trim()}\n\n${bugDescription.trim()}`,
            url: globalThis.location.href,
          });
        });
      }
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) return;
    setLoading(true);
    try {
      submitFeedback({
        category: feedbackCategory,
        message: feedbackMessage.trim(),
        userEmail: user?.email,
        userName: userName || undefined,
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const isBugValid = bugTitle.trim().length > 0 && bugDescription.trim().length > 0;
  const isFeedbackValid = feedbackMessage.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        {isCrashMode ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <BugIcon color="error" fontSize="small" />
            <Typography variant="h6">Add Details to Crash Report</Typography>
          </Box>
        ) : (
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
            <Tab icon={<BugIcon fontSize="small" />} iconPosition="start" label="Report Bug" />
            <Tab icon={<FeedbackIcon fontSize="small" />} iconPosition="start" label="Share Feedback" />
          </Tabs>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {(() => {
          if (submitted) return (
          <Alert severity="success" sx={{ mt: 1 }}>
            {(() => {
              if (isCrashMode) return 'Details added to the crash report. Thank you — this will help us fix it faster.';
              if (activeTab === 0) return 'Bug report submitted. Our team has been notified and will investigate.';
              return 'Feedback submitted. Thank you for helping us improve!';
            })()}
          </Alert>
        );
          if (activeTab === 0 || isCrashMode) return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              placeholder="Short description of the bug"
              value={bugTitle}
              onChange={e => setBugTitle(e.target.value.slice(0, BUG_TITLE_MAX))}
              required
              fullWidth
              size="small"
              inputProps={{ maxLength: BUG_TITLE_MAX }}
              helperText={`${bugTitle.length}/${BUG_TITLE_MAX}`}
              FormHelperTextProps={{ sx: { textAlign: 'right' } }}
            />
            <TextField
              label="Description"
              placeholder="What happened? What did you expect to happen?"
              value={bugDescription}
              onChange={e => setBugDescription(e.target.value.slice(0, BUG_DESC_MAX))}
              required
              fullWidth
              multiline
              rows={3}
              size="small"
              inputProps={{ maxLength: BUG_DESC_MAX }}
              helperText={`${bugDescription.length}/${BUG_DESC_MAX}`}
              FormHelperTextProps={{ sx: { textAlign: 'right' } }}
            />
            {/* C4-605: Severity selector hidden per client request — bug severity defaults to 'error'.
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Severity
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {SEVERITY_OPTIONS.map(sev => (
                  <Chip
                    key={sev}
                    label={SEVERITY_LABEL[sev]}
                    color={bugSeverity === sev ? SEVERITY_COLOR[sev] : 'default'}
                    variant={bugSeverity === sev ? 'filled' : 'outlined'}
                    size="small"
                    onClick={() => setBugSeverity(sev)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
            */}
          </Box>
        );
          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={feedbackCategory}
                label="Category"
                onChange={e => setFeedbackCategory(e.target.value as FeedbackCategory)}
              >
                {FEEDBACK_CATEGORIES.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Message"
              placeholder="Tell us what you think…"
              value={feedbackMessage}
              onChange={e => setFeedbackMessage(e.target.value.slice(0, FEEDBACK_MSG_MAX))}
              required
              fullWidth
              multiline
              rows={4}
              size="small"
              inputProps={{ maxLength: FEEDBACK_MSG_MAX }}
              helperText={`${feedbackMessage.length}/${FEEDBACK_MSG_MAX}`}
              FormHelperTextProps={{ sx: { textAlign: 'right' } }}
            />
          </Box>
        );
        })()}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          {submitted ? 'Close' : 'Cancel'}
        </Button>
        {!submitted && (
          <Button
            variant="contained"
            onClick={activeTab === 0 || isCrashMode ? handleSubmitBug : handleSubmitFeedback}
            disabled={loading || (activeTab === 0 || isCrashMode ? !isBugValid : !isFeedbackValid)}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {activeTab === 0 || isCrashMode ? 'Submit Bug Report' : 'Submit Feedback'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
